import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { useAuthState } from "react-firebase-hooks/auth";
import { db, auth } from "../../firebase";
import { useBlocks } from "../../blocks";
import { usePins } from "../../pins";
import PinButton from "../../components/PinButton";
import { RequestButton, SafetyActions } from "../../components/SafetyBits";
import {
  arePenPals,
  deleteMyAd,
  getMyAnonAdIds,
  sendKudos,
  sendKudosToAd,
  sendPenPalRequest,
  sendRequestToAd,
  withPosterNames,
  stripEmoji,
} from "../../penpal";
import Window from "../../components/Window";
import { sanitizeHtml } from "../../sanitize";
import Icon from "../../components/Icon";
import ContentWarning from "../../components/ContentWarning";
import "../../styles.css";

interface AdData {
  uid?: string; // absent on anonymous ads
  username: string;
  anonymous?: boolean;
  subject: string;
  message: string;
  timestamp: {
    seconds: number;
    nanoseconds: number;
  };
  categories?: string[];
  cw?: string; // optional content warning
}

export default function SingleAd() {
  const { id } = useParams();
  const [ad, setAd] = useState<AdData | null>(null);
  const [loading, setLoading] = useState(true);
  const [ownsAnon, setOwnsAnon] = useState(false);
  const [user] = useAuthState(auth);

  useEffect(() => {
    const fetchAd = async () => {
      if (!id) return;
      try {
        const adRef = doc(db, "penpalAds", id);
        const adSnap = await getDoc(adRef);
        if (adSnap.exists()) {
          const [named] = await withPosterNames([adSnap.data() as AdData]);
          setAd(named);
        }
      } catch (err) {
        console.error("Failed to fetch ad:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchAd();
  }, [id]);

  // Only the poster's private list says an anonymous ad is theirs.
  useEffect(() => {
    if (!user || !id || !ad?.anonymous) return;
    getMyAnonAdIds(user.uid)
      .then((ids) => setOwnsAnon(ids.includes(id)))
      .catch(() => setOwnsAnon(false));
  }, [user, id, ad?.anonymous]);

  const { ids: blockedIds } = useBlocks();
  const pins = usePins(user?.uid);
  const navigate = useNavigate();
  const [pals, setPals] = useState(false);
  useEffect(() => {
    if (user && ad?.uid && user.uid !== ad.uid) arePenPals(user.uid, ad.uid).then(setPals);
  }, [user, ad?.uid]);

  if (loading) {
    return <p className="empty">Unfolding the letter...</p>;
  }

  if (!ad || !id) {
    return (
      <Window title="error.txt">
        <p className="empty">
          <span className="empty-icon">
            <Icon name="mail" size={36} tone="pink" pop />
          </span>
          This letter got lost in the mail.{" "}
          <Link to="/board">Back to the board</Link>
        </p>
      </Window>
    );
  }

  const formattedDate = ad.timestamp?.seconds
    ? new Date(ad.timestamp.seconds * 1000).toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "";

  const anonymous = !!ad.anonymous || !ad.uid;
  const blocked = !anonymous && !!ad.uid && blockedIds.has(ad.uid);

  const sendRequest = (note: string) =>
    anonymous
      ? sendRequestToAd(user!.uid, id, note)
      : sendPenPalRequest(user!.uid, ad.uid as string, note);

  const handleSendKudos = async () => {
    if (!user) return;
    const ok = anonymous
      ? await sendKudosToAd(user.uid, id)
      : await sendKudos(user.uid, ad.uid as string);
    if (ok !== null) alert(ok ? "Kudos sent!" : "Sorry, kudos couldn't be sent.");
  };

  const isMine = anonymous ? ownsAnon : user?.uid === ad.uid;

  const handleDelete = async () => {
    if (!window.confirm("Take this ad down? This can't be undone.")) return;
    try {
      await deleteMyAd(id);
      navigate("/board");
    } catch (err) {
      console.error(err);
      alert("Sorry, the ad couldn't be deleted. Please try again.");
    }
  };

  if (blocked) {
    return (
      <Window title="letter.txt" tone="tan">
        <p className="empty">
          You've blocked the person who posted this.{" "}
          <Link to="/board">Back to the board</Link>
        </p>
      </Window>
    );
  }

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Link to="/board" className="btn btn-plain btn-sm">
          <Icon name="back" size={16} /> Back to the board
        </Link>
        {user && (
          <PinButton uid={user.uid} adId={id} pinned={pins.includes(id)} withLabel />
        )}
      </div>

      <Window title="letter.txt" tone="tan">
        <p className="mb-1 text-sm text-ink-soft">
          <Icon name="pin" size={14} />{" "}
          {anonymous ? (
            <>Posted anonymously</>
          ) : (
            <>
              Posted by <Link to={`/profile/${ad.uid}`}>@{ad.username}</Link>
            </>
          )}
          {formattedDate && ` on ${formattedDate}`}
        </p>
        <h1 className="window-heading mb-4">
          {ad.subject || "Untitled Letter"}
        </h1>

        {anonymous && isMine && (
          <p className="notice mb-4 flex items-center gap-2 text-sm">
            <Icon name="eye" size={16} /> This is your anonymous ad. Only you
            can see this note; everyone else just sees "anonymous."
          </p>
        )}

        <ContentWarning cw={ad.cw}>
          <div
            className="lined-paper"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(ad.message) }}
          />
        </ContentWarning>

        {ad.categories && ad.categories.length > 0 && (
          <div className="chip-row mt-4">
            {ad.categories.map((cat) => (
              <span key={cat} className="chip">
                {stripEmoji(cat)}
              </span>
            ))}
          </div>
        )}

        {isMine && (
          <>
            <div className="divider my-5" />
            <div className="btn-row justify-center">
              <button onClick={handleDelete} className="btn btn-plain btn-sm">
                <Icon name="close" size={14} /> Delete my ad
              </button>
            </div>
          </>
        )}

        {!isMine && (
          <>
            <div className="divider my-5" />
            <div className="btn-row justify-center">
              {pals && !anonymous ? (
                <Link to={`/send?to=${ad.uid}`} className="btn btn-blue">
                  <Icon name="mail" size={16} /> Write a letter
                </Link>
              ) : (
                <RequestButton send={sendRequest} />
              )}
              <button onClick={handleSendKudos} className="btn btn-plain">
                <Icon name="gift" size={16} /> Send kudos
              </button>
            </div>
            <p className="mt-3 text-center text-sm text-ink-soft">
              {anonymous
                ? "This writer is anonymous. Your request goes to their mailbox, and they choose whether to say hi back."
                : pals
                ? "You're pen pals, so you can write to each other."
                : "Once they accept your request, you can write letters to each other."}
            </p>
            <div className="mt-2 text-center">
              <SafetyActions
                target={
                  anonymous
                    ? { adId: id }
                    : { adId: id, userId: ad.uid, username: ad.username }
                }
                canBlock={!anonymous}
              />
            </div>
          </>
        )}
      </Window>
    </>
  );
}
